import { Combobox, Transition } from '@headlessui/react';
import { Fragment, useState, forwardRef } from 'react';
import CheckIcon from '@/assets/icons/check.svg';
import ChevronDownIcon from '@/assets/icons/chevron-down.svg';
import Chip from '../chip';

export interface OptionCombobox {
  id: string;
  value: string;
  label: string;
}

export interface BaseComboboxProps {
  label: string;
  options: OptionCombobox[];
  isMultiple?: boolean;
  value?: OptionCombobox | OptionCombobox[];
  onChange: (value: any) => void;
  errorMessage?: string;
}

const BaseCombobox = forwardRef<HTMLDivElement, BaseComboboxProps>(
  ({ label, options, isMultiple = false, value, onChange, errorMessage }, ref) => {

    const [query, setQuery] = useState('');

    const filteredOptions =
      query === ''
        ? options
        : options.filter((val) =>
          val.label
            .toLowerCase()
            .replace(/\s+/g, '')
            .includes(query.toLowerCase().replace(/\s+/g, ''))
        );

    const selectedValue = options.find((opt) => opt.id === (value as OptionCombobox)?.id);
    const selectedMultiple = Array.isArray(value) ? value : [];

    return (
      <div ref={ref} className="w-full">
        <label className="form-label font-medium dark:text-white mb-1 block">
          Select {label}:
        </label>
        {/* @ts-ignore */}
        <Combobox value={value} onChange={onChange} multiple={isMultiple} by="id">
          {({ open }) => (
            <div className="relative w-full">
              <div className="relative w-full cursor-default overflow-hidden text-left rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 focus-within:ring-2 focus-within:ring-brand-500 focus-within:border-transparent">
                <Combobox.Button as="span">
                  <Combobox.Input
                    className="w-full py-2 pl-3 pr-10 text-sm text-gray-900 dark:text-white bg-transparent focus:outline-none"
                    value={isMultiple ? '' : selectedValue?.label || ''}
                    displayValue={(val: any) => (isMultiple ? '' : val?.label)}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder={`Search ${label} name`}
                    autoComplete="off"
                  />
                </Combobox.Button>

                <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronDownIcon className="h-5 w-5 text-gray-400 dark:text-gray-300" aria-hidden="true" />
                </Combobox.Button>
                {isMultiple && selectedMultiple.length > 0 && (
                  <div className="flex gap-1 p-2 flex-wrap">
                    {selectedMultiple.map((data: OptionCombobox, key) => (
                      <Chip key={key} label={data.label} />
                    ))}
                  </div>
                )}
              </div>

              <Transition
                show={open}
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                afterLeave={() => setQuery('')}
              >
                <Combobox.Options className="absolute mt-1 max-h-60 w-full overflow-auto rounded-xl bg-white dark:bg-gray-800 py-1 text-sm shadow-theme-lg border border-gray-200 dark:border-gray-700 focus:outline-none z-[999]">
                  {filteredOptions.length === 0 && query !== '' ? (
                    <div className="relative cursor-default select-none px-4 py-2 text-gray-700 dark:text-gray-300">
                      Nothing found.
                    </div>
                  ) : (
                    filteredOptions.map((item) => (
                      <Combobox.Option
                        key={item.id}
                        value={item}
                        className={({ active }) =>
                          `relative cursor-default select-none py-2.5 pl-10 pr-4 transition-colors ${active ? 'bg-brand-50 text-brand-600 dark:bg-brand-500/10 dark:text-brand-400' : 'text-gray-900 dark:text-gray-100'
                          }`
                        }
                      >
                        {({ selected, active }) => (
                          <>
                            <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                              {item.label}
                            </span>
                            {selected && (
                              <span
                                className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-brand-600 dark:text-brand-400' : 'text-brand-500'
                                  }`}
                              >
                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                              </span>
                            )}
                          </>
                        )}
                      </Combobox.Option>
                    ))
                  )}
                </Combobox.Options>
              </Transition>
            </div>
          )}
        </Combobox>
        {errorMessage && <p className="text-red-500 text-xs mt-1">{errorMessage}</p>}
      </div>
    );
  }
);

BaseCombobox.displayName = 'BaseCombobox';

export default BaseCombobox;
